import { Component, Input } from '@angular/core';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PurchasingService } from '../../services/purchasing.service';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { NgxDropzoneChangeEvent } from 'ngx-dropzone';

@Component({
	selector: 'app-vendor-po-upload',
	templateUrl: './vendor-po-upload.component.html',
	styleUrls: ['./vendor-po-upload.component.scss'],
})
export class VendorPoUploadComponent {
	@Input() vendor: any = null;

	files: File[] = [];
	activeFile: File | null = null;
	/** Parsed workbook kept after drop so user can switch sheet / header row without re-uploading. */
	excelWorkbook: XLSX.WorkBook | null = null;
	sheetNames: string[] = [];
	selectedSheetName = '';
	/** 1-based row that contains column headings (data starts on the following row). */
	headerRowNumber = 1;
	showParseOptions = false;

	uploadedHeaders: string[] = [];
	uploadedRawData: any[] = [];
	columnMappings: { [header: string]: string } = {};
	availableFieldTypes: string[] = ['stockid', 'qty_purchase', 'price', 'description', 'ignore'];
	processing = false;
	uploadReady = false;
	creating = false;
	poError = '';
	poSuccess = '';
	user: any = {};
	builtLines: { stockid: string; quantity: number; price: number; description: string }[] = [];

	color = 'blue';

	private readonly poUploadAllowedExtensions = ['.csv', '.xlsx', '.xls'];

	private readonly poFieldSynonyms: Record<string, string[]> = {
		stockid: ['stockid', 'sku', 'itemnumber', 'itemnum', 'item', 'partnumber', 'partnum', 'part', 'productcode', 'product'],
		qty_purchase: ['qty', 'quantity', 'qtypurchase', 'orderqty', 'qtyordered', 'purchaseqty', 'orderquantity'],
		price: ['price', 'unitprice', 'sellprice', 'listprice', 'netprice', 'unitcost', 'cost'],
		description: ['description', 'desc', 'longdesc', 'title', 'name'],
	};

	constructor(
		private purchasingService: PurchasingService,
		private globalSearchService: GlobalSearchService
	) {
		this.color = this.globalSearchService.getColor();
		this.globalSearchService.user.subscribe((u: any) => {
			this.user = u?.user || u || {};
		});
	}

	get dropzoneThemeColor(): string {
		const allowed = new Set(['blue', 'azure', 'purple', 'green', 'red']);
		const c = String(this.color || 'blue')
			.toLowerCase()
			.trim();
		return allowed.has(c) ? c : 'blue';
	}

	fieldTypeLabel(type: string): string {
		const labels: Record<string, string> = {
			stockid: 'Item # (stockid)',
			qty_purchase: 'Quantity',
			price: 'Unit price',
			description: 'Description',
			ignore: '— Ignore —',
		};
		return labels[type] || type;
	}

	onDropzoneChange(event: NgxDropzoneChangeEvent): void {
		if (!event) {
			return;
		}
		if (event.rejectedFiles?.length) {
			const names = event.rejectedFiles.map((f) => f.name).join(', ');
			this.globalSearchService.showNotification(
				`Not added (type or size): ${names}`,
				'warning',
				'bottom',
				'right'
			);
		}
		if (!event.addedFiles?.length) {
			return;
		}
		const f = event.addedFiles[0];
		const lower = f.name.toLowerCase();
		if (!this.poUploadAllowedExtensions.some((ext) => lower.endsWith(ext))) {
			this.globalSearchService.showNotification(
				`Use CSV or Excel only: ${f.name}`,
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		if (event.addedFiles.length > 1) {
			this.globalSearchService.showNotification(
				'Only one file at a time — using the first file.',
				'info',
				'bottom',
				'right'
			);
		}

		this.clearParsedData();
		this.activeFile = f;
		this.files = [f];
		this.showParseOptions = true;
		this.headerRowNumber = 1;

		if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
			this.processing = true;
			this.readFileAsArrayBuffer(f)
				.then((buf) => {
					try {
						this.excelWorkbook = XLSX.read(buf, { type: 'array', cellDates: true, dense: false });
						this.sheetNames = [...(this.excelWorkbook.SheetNames || [])];
						this.selectedSheetName = this.sheetNames[0] || '';
					} catch {
						this.globalSearchService.showNotification(
							'Could not read that Excel file.',
							'danger',
							'bottom',
							'right'
						);
						this.clearAll();
					}
					this.processing = false;
				})
				.catch(() => {
					this.processing = false;
					this.globalSearchService.showNotification('Could not read file.', 'danger', 'bottom', 'right');
					this.clearAll();
				});
		} else {
			this.excelWorkbook = null;
			this.sheetNames = [];
			this.selectedSheetName = '';
			this.processing = false;
		}
	}

	onRemove(file: File): void {
		this.files = this.files.filter((x) => x !== file);
		if (!this.files.length) {
			this.clearAll();
		}
	}

	clearParsedData(): void {
		this.uploadedRawData = [];
		this.uploadedHeaders = [];
		this.columnMappings = {};
		this.uploadReady = false;
		this.builtLines = [];
		this.poError = '';
		this.poSuccess = '';
	}

	clearAll(): void {
		this.files = [];
		this.activeFile = null;
		this.excelWorkbook = null;
		this.sheetNames = [];
		this.selectedSheetName = '';
		this.headerRowNumber = 1;
		this.showParseOptions = false;
		this.clearParsedData();
	}

	parseSpreadsheet(): void {
		if (!this.activeFile) {
			this.globalSearchService.showNotification('Add a file first.', 'info', 'bottom', 'right');
			return;
		}
		const lower = this.activeFile.name.toLowerCase();
		if ((lower.endsWith('.xlsx') || lower.endsWith('.xls')) && !this.selectedSheetName) {
			this.globalSearchService.showNotification('Pick a sheet to import.', 'warning', 'bottom', 'right');
			return;
		}

		this.processing = true;
		this.clearParsedData();

		const run = async (): Promise<void> => {
			try {
				let rows: Record<string, string>[] = [];
				const headerRow0 = Math.max(0, (Number(this.headerRowNumber) || 1) - 1);

				if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
					if (!this.excelWorkbook) {
						this.globalSearchService.showNotification('Reload the Excel file.', 'warning', 'bottom', 'right');
						return;
					}
					const ws = this.excelWorkbook.Sheets[this.selectedSheetName];
					if (!ws) {
						this.globalSearchService.showNotification('Sheet not found.', 'danger', 'bottom', 'right');
						return;
					}
					const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
						range: headerRow0,
						defval: '',
						raw: false,
					});
					rows = raw.map((row) => this.normalizeDataRow(row));
				} else if (lower.endsWith('.csv')) {
					const text = await this.readFileAsTextUtf8(this.activeFile);
					const normalized = text.replace(/^\uFEFF/, '');
					const wb = XLSX.read(normalized, { type: 'string', cellDates: true, raw: false });
					const sn = wb.SheetNames[0];
					if (!sn) {
						this.globalSearchService.showNotification('CSV appears empty.', 'warning', 'bottom', 'right');
						return;
					}
					const ws = wb.Sheets[sn];
					const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, {
						range: headerRow0,
						defval: '',
						raw: false,
					});
					rows = raw.map((row) => this.normalizeDataRow(row));
				}

				this.uploadedRawData = rows;
				this.uploadedHeaders = rows.length > 0 ? Object.keys(rows[0]) : [];
				this.uploadedHeaders.forEach((h) => {
					if (this.columnMappings[h] === undefined) {
						this.columnMappings[h] = 'ignore';
					}
				});
				this.applyGreedyAutoMap();
				this.uploadReady = rows.length > 0;
				if (!rows.length) {
					this.globalSearchService.showNotification(
						'No data rows after the header row. Increase header row # if titles sit above column names (e.g. use 2).',
						'warning',
						'bottom',
						'right'
					);
				}
			} catch {
				this.globalSearchService.showNotification('Could not parse file.', 'danger', 'bottom', 'right');
			} finally {
				this.processing = false;
			}
		};

		void run();
	}

	private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e) => resolve((e.target as FileReader).result as ArrayBuffer);
			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(file);
		});
	}

	private readFileAsTextUtf8(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(String(reader.result ?? ''));
			reader.onerror = () => reject(reader.error);
			reader.readAsText(file, 'UTF-8');
		});
	}

	private normalizeHeaderKey(k: string): string {
		return String(k).replace(/^\uFEFF/, '').trim();
	}

	private normalizeDataRow(row: Record<string, unknown>): Record<string, string> {
		const o: Record<string, string> = {};
		for (const k of Object.keys(row)) {
			if (k === '__rowNum__') {
				continue;
			}
			const nk = this.normalizeHeaderKey(k);
			if (nk === '') {
				continue;
			}
			const v = row[k];
			o[nk] = v === null || v === undefined ? '' : String(v).trim();
		}
		return o;
	}

	private normalizeForScore(s: string): string {
		return String(s || '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '');
	}

	private scorePoFieldHeader(field: string, header: string): number {
		const hn = this.normalizeForScore(header);
		if (!hn) {
			return 0;
		}
		const fn = this.normalizeForScore(field);
		if (hn === fn) {
			return 100;
		}
		let best = 0;
		const syns = this.poFieldSynonyms[field];
		if (syns) {
			for (const syn of syns) {
				const sn = this.normalizeForScore(syn);
				if (!sn) {
					continue;
				}
				if (hn === sn) {
					best = Math.max(best, 92);
				} else if (hn.includes(sn) && sn.length >= 4) {
					best = Math.max(best, 72);
				} else if (hn.includes(sn) && sn.length >= 3) {
					best = Math.max(best, 52);
				} else if (sn.includes(hn) && hn.length >= 4) {
					best = Math.max(best, 58);
				}
			}
		}
		return best;
	}

	/** One column → one target field; each target used once (same as order / inventory upload). */
	private applyGreedyAutoMap(): void {
		const targets = this.availableFieldTypes.filter((t) => t !== 'ignore');
		const fieldIndex = (f: string) => targets.indexOf(f);
		const pairs: { field: string; header: string; score: number }[] = [];
		for (const header of this.uploadedHeaders) {
			for (const field of targets) {
				const sc = this.scorePoFieldHeader(field, header);
				if (sc >= 40) {
					pairs.push({ field, header, score: sc });
				}
			}
		}
		pairs.sort((a, b) => {
			if (b.score !== a.score) {
				return b.score - a.score;
			}
			return fieldIndex(a.field) - fieldIndex(b.field);
		});
		const usedHeaders = new Set<string>();
		const usedFields = new Set<string>();
		this.uploadedHeaders.forEach((h) => {
			this.columnMappings[h] = 'ignore';
		});
		for (const p of pairs) {
			if (usedHeaders.has(p.header) || usedFields.has(p.field)) {
				continue;
			}
			this.columnMappings[p.header] = p.field;
			usedHeaders.add(p.header);
			usedFields.add(p.field);
		}
	}

	private parseNumericCell(v: unknown): number {
		if (v === null || v === undefined || v === '') {
			return NaN;
		}
		if (typeof v === 'number' && !isNaN(v)) {
			return v;
		}
		let s = String(v).trim();
		s = s.replace(/[$£€]/g, '').replace(/\s/g, '');
		if (/%\s*$/.test(s)) {
			s = s.replace(/%\s*$/, '');
		}
		s = s.replace(/,/g, '');
		const n = parseFloat(s);
		return isNaN(n) ? NaN : n;
	}

	applyMappings(): { stockid: string; quantity: number; price: number; description: string }[] {
		const details: { stockid: string; quantity: number; price: number; description: string }[] = [];
		this.uploadedRawData.forEach((row) => {
			const mapped: Record<string, string> = {};
			Object.keys(row).forEach((header) => {
				const field = this.columnMappings[header];
				if (field && field !== 'ignore') {
					mapped[field] = row[header];
				}
			});
			const qty = this.parseNumericCell(mapped['qty_purchase']);
			if ((mapped['stockid'] || '').toString().trim() && !isNaN(qty) && qty !== 0) {
				details.push({
					stockid: (mapped['stockid'] || '').toString().trim(),
					quantity: qty,
					price: this.parseNumericCell(mapped['price']) || 0,
					description: (mapped['description'] || '').toString().trim(),
				});
			}
		});
		return details;
	}

	hasRequiredMappings(): boolean {
		const used = new Set<string>();
		Object.keys(this.columnMappings).forEach((header) => {
			const v = this.columnMappings[header];
			if (v && v !== 'ignore') {
				used.add(v);
			}
		});
		return used.has('stockid') && used.has('qty_purchase');
	}

	buildLines(): void {
		this.builtLines = this.applyMappings().slice();
		this.poError = '';
	}

	addLine(): void {
		this.builtLines.push({ stockid: '', quantity: 0, price: 0, description: '' });
	}

	removeLine(index: number): void {
		this.builtLines.splice(index, 1);
	}

	downloadPoUploadTemplate(): void {
		const header = 'stockid,qty_purchase,price,description';
		const sample1 = 'ITEM001,10,25.00,Widget';
		const sample2 = 'ITEM002,5,10.50,Part B';
		const csv = [header, sample1, sample2].join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		FileSaver.saveAs(blob, 'vendor_po_upload_template.csv');
	}

	createPo(): void {
		if (!this.vendor?.supplierid || !this.user?.userid) {
			this.poError = 'Vendor or user not set';
			return;
		}
		const validLines = this.builtLines.filter(
			(l) => (l.stockid || '').toString().trim() && !isNaN(l.quantity) && l.quantity !== 0
		);
		if (!validLines.length) {
			this.poError = 'No valid lines (stockid and quantity required per line)';
			return;
		}
		const details = validLines.map((l) => ({
			stockid: (l.stockid || '').toString().trim(),
			quantity: l.quantity,
			price: l.price || 0,
			description: (l.description || '').toString().trim(),
		}));
		const tomorrow = new Date();
		tomorrow.setDate(tomorrow.getDate() + 1);
		const loc = this.user.defaultlocation?.loccode || this.user.loccode || '';
		const header: any = {
			comments: 'Created from upload',
			initiator: this.user.userid || this.user.user?.userid || '',
			refrenceno: '',
			warehouse: loc,
			deliverydate: tomorrow.toISOString ? tomorrow.toISOString().slice(0, 10) : tomorrow.toLocaleDateString('en-CA'),
			deladd1: '',
			deladd2: '',
			deladd3: '',
			deladd4: '',
			deladd5: '',
			deladd6: '',
			address1: '',
			address2: '',
			address3: '',
			address4: '',
			address5: '',
			address6: '',
		};
		const lineDetails = details.map((d) => ({
			stockid: d.stockid,
			quantity: d.quantity,
			price: d.price,
			description: d.description || '',
			notes: '',
			line_notes: '',
			fetcharge: 0,
		}));
		this.creating = true;
		this.poError = '';
		this.poSuccess = '';
		this.purchasingService.createPurchaseOrder(this.vendor, header, lineDetails, this.user).subscribe(
			(res: any) => {
				this.creating = false;
				if (res?.success && res?.orderno) {
					this.poSuccess = 'PO #' + res.orderno + ' created';
					this.globalSearchService.showNotification(this.poSuccess, 'success', 'bottom', 'right');
					this.clearAll();
				} else {
					this.poError = res?.message || 'Create failed';
				}
			},
			(err) => {
				this.creating = false;
				this.poError = err?.error?.message || err?.message || 'Request failed';
			}
		);
	}
}
